import { forwardRef } from 'react';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { LinkProps } from 'react-router-dom';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger' | 'gold' | 'inverse';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Renders a spinner, disables interaction, and keeps the label for width stability. */
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
}

// One accent does the work: the brand blue carries the single primary action per
// view and everything else recedes to outline or ghost. Bordered variants use
// `input-border` rather than the decorative hairline, so a control's edge clears
// the 3:1 contrast WCAG 1.4.11 asks for. Motion is a 1px press, not a bounce.
const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-white hover:opacity-90 active:translate-y-px shadow-xs',
  secondary:
    'bg-background-alt text-text-primary border border-input-border hover:bg-background-card active:translate-y-px',
  outline:
    'border border-input-border bg-transparent text-text-primary hover:bg-background-alt active:translate-y-px',
  ghost:
    'bg-transparent text-text-secondary hover:bg-background-alt hover:text-text-primary',
  danger:
    'bg-destructive text-destructive-foreground hover:opacity-90 active:translate-y-px shadow-xs',
  // The logo gold, carrying ink type. For the primary action on a dark surface,
  // where the brand blue is too close to the background to read as a button.
  gold:
    'bg-gold text-[#17181C] hover:opacity-90 active:translate-y-px shadow-xs',
  // For a secondary action on a dark surface.
  inverse:
    'border border-white/25 bg-transparent text-white hover:bg-white/10 active:translate-y-px',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3.5 text-caption gap-1.5 rounded-md',
  md: 'h-11 px-5 text-[0.9375rem] gap-2 rounded-md',
  lg: 'h-13 px-7 text-body gap-2.5 rounded-lg',
  // Square, icon-only. Callers used to reach for `className="h-11 w-11 px-0"`,
  // but `cn` is a plain joiner: `px-5` and `px-0` both landed in the class list
  // and the stylesheet picked `px-5`, leaving 4px of content box and crushing an
  // 18px chevron down to a dot. A real size removes the need to override.
  icon: 'h-11 w-11 rounded-md',
  'icon-sm': 'h-9 w-9 rounded-md',
};

const BASE = cn(
  'inline-flex items-center justify-center font-medium font-sans whitespace-nowrap',
  'transition-all duration-200 ease-editorial',
  'disabled:pointer-events-none disabled:opacity-50',
  // Icons must never be squeezed by a tight content box.
  '[&_svg]:shrink-0',
);

/**
 * The button's appearance, detached from the `<button>` element.
 *
 * Something that navigates must be an `<a>`, not a button wrapped in a link —
 * nesting the two produces two tab stops for one control and is announced twice
 * by a screen reader. `LinkButton` below covers the common case; this helper is
 * for anywhere else that needs the same styling on a different element.
 */
export const buttonStyles = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
} = {}): string => cn(BASE, VARIANTS[variant], SIZES[size], fullWidth && 'w-full', className);

const Spinner = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
    <path className="opacity-90" d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, leadingIcon, trailingIcon, fullWidth, className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonStyles({ variant, size, fullWidth, className })}
      {...props}
    >
      {loading ? <Spinner /> : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  );
});

interface LinkButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export type LinkButtonProps = LinkButtonBaseProps & Omit<LinkProps, 'className'> & { className?: string };

/** A router link that looks like a button. Use for anything that navigates. */
export const LinkButton = ({
  variant,
  size,
  fullWidth,
  leadingIcon,
  trailingIcon,
  className,
  children,
  ...props
}: LinkButtonProps) => (
  <Link className={buttonStyles({ variant, size, fullWidth, className })} {...props}>
    {leadingIcon}
    {children}
    {trailingIcon}
  </Link>
);

export type ExternalLinkButtonProps = LinkButtonBaseProps & AnchorHTMLAttributes<HTMLAnchorElement>;

/** The same, for `mailto:`, `tel:` and links off the site. */
export const ExternalLinkButton = ({
  variant,
  size,
  fullWidth,
  leadingIcon,
  trailingIcon,
  className,
  children,
  ...props
}: ExternalLinkButtonProps) => (
  <a className={buttonStyles({ variant, size, fullWidth, className })} {...props}>
    {leadingIcon}
    {children}
    {trailingIcon}
  </a>
);

export default Button;
